import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {ResourceService} from "../../../../service-data/resource.service";
import {Draft} from "../../../../../model/Draft";
import {Resource} from "../../../../../model/Resource";
import {
  beginImageRequest,
  endImageRequest,
  HIDDEN_IMAGES_ELEMENT_ID,
  invalidateResourceImages
} from "../../../../service-ui/resource-image-cache";
import {bestAvailableStage, PdfArtifactStatus, stagedImageId} from "../../../../service-ui/pdf-artifact-stage";
import {StageWatch} from "../../../../service-ui/staged-preview-image";
import {paintCoverWhitePlate} from "../../../../service-ui/cover-white-plate";

/**
 * The Draft cover preview image.
 *
 * Used for the thumbnail in `ProjectsComponent`, `ProjectDetailsComponent`,
 * `ClassicProjectThumbnailComponent` and `ProjectDraftThumbnailComponent`, and
 * for the cover surface of the Draft editor — so everything said below about
 * a preview that never appears applied to all of them at once.
 *
 * ── Why this component now listens for RESOURCE updates ──────────────────
 *
 * A page image cannot be rendered until the uploaded PDF is in S3, and the
 * Resource row exists before that. So the first attempt after an upload can
 * legitimately return nothing, and something has to try again afterwards.
 *
 * Nothing did. `ngOnChanges` is the only trigger, and it fires on @Input
 * identity — but a RESOURCE notification is applied by
 * `AbstractLiveDataService.addUpdateLocalItem`, which mutates the cached
 * Resource **in place**. The `draft` binding therefore never changes
 * identity, `ngOnChanges` never fires, and the preview stayed blank until the
 * user reloaded the page.
 *
 * `resourceService.updateNotifier` is the event that mutation publishes. The
 * component now subscribes to it and, when the Resource it is displaying is
 * the one that changed, invalidates the cached `<img>` and asks again. That is
 * the "request the image as soon as the UPDATE is received" behaviour.
 */
@Component({
  selector: 'app-project-draft-cover-image',
  templateUrl: './project-draft-cover-image.component.html',
  styleUrls: ['./project-draft-cover-image.component.css']
})
export class ProjectDraftCoverImageComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('draftCoverImage', { static: true }) draftCoverImage;
  @Input() draft: Draft;
  /**
   * Optional hard-coded pixel size overrides used by callers that
   * render this component in a slot smaller than the default
   * ~190×304 projects-thumbnail size (e.g. the cart's grid and
   * table views). When provided, they are bound to the img's
   * `max-width` / `min-height` / `max-height` via inline style
   * bindings which win over the scoped CSS defaults in
   * `.component.css`. When omitted (the ProjectsComponent case)
   * the original scoped 190×304 constraints apply and nothing
   * about the projects display changes.
   */
  @Input() width: number;
  @Input() height: number;

  displayScale = 1.0;

  Math = Math;

  private onDestroySubject: Subject<boolean> = new Subject<boolean>();

  /**
   * Stops an unrelated Resource edit re-requesting every thumbnail on the
   * projects grid. `updateNotifier` fires for every Resource row, and
   * notifications are broadcast to every session on the role
   * (`shared/Notifications.md` §5), so reacting unfiltered is one HTTP call
   * per visible thumbnail per unrelated edit.
   */
  private stageWatch = new StageWatch();

  /**
   * Which artefact stage this thumbnail should be showing.
   *
   * Draft side, so `standardized` is deliberately left undefined: a
   * `DraftCover` has no such field, and that absence is what tells
   * `pdf-artifact-stage` a PREPARED render is already final here. Passing
   * `standardized: ''` instead would mark every finished Draft preliminary
   * forever, because a Draft can never reach STANDARDIZED.
   */
  private artifactStatus(): PdfArtifactStatus {
    const resourceId = this.displayedResourceId();
    const resource = resourceId ? this.resourceService.getLocalItem(resourceId) : null;
    return { resourceId: resourceId, prepared: resource ? resource.prepared : undefined };
  }

  constructor(private resourceService: ResourceService) {
  }

  ngOnInit() {
    this.refreshImagePosition();

    // The RESOURCE UPDATE says the PDF has been processed. It is the only
    // signal we get that an image which was not renderable a moment ago now
    // is, so drop the cached (empty) entry and ask again.
    this.resourceService.updateNotifier
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe((resource: Resource) => this.onResourceUpdated(resource));
  }

  ngOnDestroy() {
    this.onDestroySubject.next(true);
    this.onDestroySubject.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.refreshImagePosition();
  }

  /** The Resource this component is currently showing, or 0. */
  private displayedResourceId(): number {
    if (!this.draft) { return 0; }
    if (this.draft.bindingType === 'SADDLE_STITCH') {
      return (this.draft.draftBody && this.draft.draftBody.resource_ID) || 0;
    }
    return (this.draft.draftCover && this.draft.draftCover.resource_ID) || 0;
  }

  private onResourceUpdated(resource: Resource): void {
    if (!resource || !resource.getId) { return; }
    const showing = this.displayedResourceId();
    if (!showing || resource.getId() !== showing) { return; }

    // Only the cached entries for THIS Resource go. Invalidating more would
    // make every other thumbnail on a projects page re-request at once.
    //
    // And only when something visible changed. The cache id now carries the
    // artefact stage, so an improvement already produces a different id and
    // fetches itself; invalidation is for what the id cannot express - the
    // same stage's bytes changing under a re-upload.
    if ( this.stageWatch.changed(this.artifactStatus()) ) {
      invalidateResourceImages(showing);
    }
    this.refreshImagePosition();
  }

  refreshImagePositionDetails(cachedImage: any) {
    // A cached element with no `src` is not a rendered image — it is the
    // placeholder this component used to leave behind. Copying its empty src
    // onto the visible element is what blanked the preview, so ignore it.
    if (!cachedImage || !cachedImage.src) {
      return;
    }
    this.draftCoverImage.nativeElement.style.display = "block";
    this.draftCoverImage.nativeElement.src = cachedImage.src;

    let naturalWidth = cachedImage.naturalWidth;
    if ( !naturalWidth ) {
      naturalWidth = cachedImage.width;
    }

    let naturalHeight = cachedImage.naturalHeight;
    if ( !naturalHeight ) {
      naturalHeight = cachedImage.height;
    }

    const clipWidth = this.draftCoverImage.nativeElement.parentNode.clientWidth - 2;
    const clipHeight = this.draftCoverImage.nativeElement.parentNode.clientHeight - 2;

    const pageScreenWidth = ( naturalWidth * this.displayScale * this.draft.draftCover.documentScale);
    if ( pageScreenWidth ) {
      this.draftCoverImage.nativeElement.style.width = pageScreenWidth + 'px';
      const pageScreenHeight = ( naturalHeight * this.displayScale * this.draft.draftCover.documentScale);

      this.draftCoverImage.nativeElement.style.height = pageScreenHeight + 'px';

      this.draftCoverImage.nativeElement.style.left = ( ( clipWidth - pageScreenWidth ) / 2 + this.draft.draftCover.horizontalShift * this.displayScale ) + 'px';
      this.draftCoverImage.nativeElement.style.top = ( ( clipHeight - pageScreenHeight ) / 2 + this.draft.draftCover.verticalShift * this.displayScale ) + 'px';
    }

    // After the geometry above, so the plate measures the box this element
    // actually ended up with. Also covers the case where the src did not
    // change: an identical assignment fires no `load`, so the handler alone
    // would leave the previous plate in place.
    this.onCoverImageLoaded();
  }

  /**
   * Put paper under the artwork.
   *
   * Bound to the img's `load` event because the plate needs two facts that
   * do not exist before it fires: the render's natural size, and the
   * laid-out size of the box it has to sit inside.
   */
  onCoverImageLoaded(): void {
    if ( !this.draftCoverImage ) {
      return;
    }
    paintCoverWhitePlate(this.draftCoverImage.nativeElement);
  }

  /** Attach the load/error handlers, or use the image straight away. */
  private useCachedImage(cachedImage: any, imageId: string) {
    if ( cachedImage.complete ) {
      this.refreshImagePositionDetails(cachedImage);
    } else {
      cachedImage.addEventListener('load', _ => {
        this.refreshImagePositionDetails(cachedImage);
      });
      cachedImage.addEventListener('error', _ => {
        console.log(`Error loading image ${imageId}`);
      });
    }
  }

  refreshImagePosition() {
    if ( !this.draft || !this.draft.draftCover ) {
      return;
    }
    const resourceId = this.displayedResourceId();

    if ( !resourceId ) {
      this.draftCoverImage.nativeElement.src = '';
      return;
    }

    // ⚠ The stage suffix is what makes an improved render reach the screen.
    // Without it the ORIGINAL image cached before preparation finished is
    // found again afterwards, read as a cache hit, and the better PREPARED
    // render is never requested. It also removes any dependence on the server
    // returning a different signed URL for the two versions
    // (`shared/PdfArtifactImages.md` Q3, still unanswered).
    const imageId = stagedImageId('resource-' + resourceId, bestAvailableStage(this.artifactStatus()));

    const cachedImage: any = document.getElementById(imageId);
    if ( cachedImage ) {
      this.useCachedImage(cachedImage, imageId);
      return;
    }

    const hiddenImagesElement: any = document.getElementById(HIDDEN_IMAGES_ELEMENT_ID);
    if ( !hiddenImagesElement ) {
      return;
    }

    // ⚠ Do NOT append a placeholder under `imageId` here.
    //
    // That is what this component used to do, and it is why an upload whose
    // image was not ready yet showed a permanently blank preview: the src-less
    // element became a cache hit for every later attempt, so the request was
    // never repeated, and `complete` is true for a src-less image so it was
    // also treated as loaded. The in-flight set does the job the placeholder
    // was really there for — stopping `ngOnChanges`, which fires on every
    // change-detection pass, from issuing a request per tick — without
    // leaving anything behind to be mistaken for a result.
    if ( !beginImageRequest(imageId) ) {
      return;
    }

    this.resourceService.getCroppedImageURL(resourceId, 'COLOR', 72, 1, this.draft.draftBody.width, this.draft.draftBody.height, this.draft.draftCover.horizontalShift, this.draft.draftCover.verticalShift, this.draft.draftBody.width + this.draft.draftBody.spine, 0, 0, 0, 'CENTER_CENTER').subscribe(
      imageURL => {
        endImageRequest(imageId);
        if ( !imageURL ) {
          // Normal before the PDF is processed. Nothing is cached, so the
          // RESOURCE UPDATE will cause a genuine retry rather than finding a
          // poisoned entry.
          //
          // Unless this session will never GET a RESOURCE UPDATE, which is
          // the case for serviceProvider. Then waiting is waiting forever,
          // so fall back to re-reading the row. No-ops for every other role.
          this.resourceService.ensureProcessedResource(resourceId);
          return;
        }
        // Re-check: another component may have cached this id while we waited.
        let element: any = document.getElementById(imageId);
        if ( !element ) {
          element = document.createElement("img");
          element.id = imageId;
          element.src = imageURL;
          hiddenImagesElement.appendChild(element);
        }
        this.useCachedImage(element, imageId);
      },
      err => {
        endImageRequest(imageId);
        console.log(`Error requesting image ${imageId}`, err);
      }
    );
  }
}

